import * as React from 'react';
import { Button, Card, Eyebrow, Heading, ProgressBar, Stripe, Text } from '../../ui/index.js';
import { Focusable, FocusZone } from '../focus/Focusable.js';
import { useAppConfig } from '../../api/hooks.js';
import { native } from '../../platform/tauri.js';
import { isDesktop, platform } from '../../platform/index.js';
import { checkForUpdate, installUpdate, useAppUpdate } from '../../lib/appUpdate.js';

/**
 * Blocks the app when it is older than the minimum version in `/v1/app/config`
 * (`04-launcher-and-patcher.md` section 1.2).
 *
 * The minimum lives on the server, so a broken release can be shut off without waiting for
 * anyone to update by choice. That only works if this screen is also the way out: on the desktop
 * it drives the updater itself rather than asking someone to go and find an installer.
 */
export function UpdateRequiredGate() {
  const { data: config } = useAppConfig();
  const [appVersion, setAppVersion] = React.useState<string | null>(null);
  const update = useAppUpdate();

  React.useEffect(() => {
    native
      .deviceInfo()
      .then((info) => setAppVersion(info.appVersion))
      .catch(() => setAppVersion(null));
  }, []);

  const minimum = config?.minimumVersions?.[platform as 'windows' | 'linux' | 'android'];
  const blocked = Boolean(appVersion && minimum && compareVersions(appVersion, minimum) < 0);

  // The six hourly check is not worth waiting for when nothing works until it lands.
  React.useEffect(() => {
    if (blocked && isDesktop) void checkForUpdate();
  }, [blocked]);

  if (!blocked) return null;

  const action = isDesktop ? desktopAction(update) : ANDROID_ACTION;

  return (
    <div className="gb-sheet-backdrop" role="alertdialog" aria-modal="true">
      {/* Nothing behind this is usable, so focus does not leave it: there is one way on, and
          a controller has to be able to reach it. */}
      <FocusZone focusKey="update-gate" trap direction="column" className="gb-osk__panel">
        <Card size="4" variant="solid" className="gb-stack">
          <Stripe />
          <Eyebrow tone="lemon">{`Build ${appVersion} · minimum ${minimum}`}</Eyebrow>
          <Heading size="5">{'This build is too old to keep going.'}</Heading>
          <Text render={<p />} color="gray">
            {action.explanation}
          </Text>

          {update.status === 'downloading' ? (
            <ProgressBar value={update.progress} label={`Downloading version ${update.version}`} />
          ) : null}

          {update.status === 'failed' && update.error ? (
            <Text render={<p />} size="1" color="gray">
              {`The update could not be fetched: ${update.error}`}
            </Text>
          ) : null}

          <div className="gb-row">
            <Focusable
              autoFocus
              onSelect={action.onSelect}
              selectLabel={action.label}
              focusKey="update-gate-restart"
              disabled={action.busy}
              className="gb-focusable--flat"
            >
              <Button size="3" disabled={action.busy}>
                {action.label}
              </Button>
            </Focusable>
          </div>
        </Card>
      </FocusZone>
    </div>
  );
}

interface GateAction {
  label: string;
  explanation: string;
  busy: boolean;
  onSelect: () => void;
}

/**
 * Android has no self-update: the APK comes from the site and is installed by the system
 * (`04-launcher-and-patcher.md` section 1.2), so the only thing this screen can offer is the
 * reload that picks up a build already installed underneath it.
 */
const ANDROID_ACTION: GateAction = {
  label: 'Restart now',
  explanation:
    'Restart to pick up the current one. Everything you have installed stays where it is.',
  busy: false,
  onSelect: () => window.location.reload(),
};

function desktopAction(update: ReturnType<typeof useAppUpdate>): GateAction {
  const stays = 'Everything you have installed stays where it is.';
  switch (update.status) {
    case 'checking':
      return {
        label: 'Looking for it',
        explanation: `Looking for the current build. ${stays}`,
        busy: true,
        onSelect: () => {},
      };
    case 'downloading':
      return {
        label: 'Downloading',
        explanation: `Fetching version ${update.version}. ${stays}`,
        busy: true,
        onSelect: () => {},
      };
    case 'installing':
      return {
        label: 'Restarting',
        explanation: `Installing version ${update.version}. ${stays}`,
        busy: true,
        onSelect: () => {},
      };
    case 'ready':
      return {
        label: 'Restart now',
        explanation: `Version ${update.version} is downloaded and restarting installs it. ${stays}`,
        busy: false,
        onSelect: () => void installUpdate(),
      };
    default:
      // Idle with nothing found, or a check that failed. Trying again is the only move, and it
      // is the same call either way: it downloads first when there is nothing pending.
      return {
        label: 'Try again',
        explanation: `This build has to update before it can go on. ${stays}`,
        busy: false,
        onSelect: () => void installUpdate(),
      };
  }
}

/** Compares two dotted versions. Returns a negative number when `a` is older than `b`. */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map((n) => Number.parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => Number.parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}
